import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import GeneralDialog from './general-dialog';
import { GridStateKey, UserSettings, ViewType } from '../../types';
import { saveViewType, UserKeys } from '../api/users-api';
import { handleErrors } from '../utils/errors';
import { useStore } from '../components/state/zustand-store';
import { useAnalytics } from './analytics/create-events';
import * as analytics from './analytics/analytics.json';
import { useGridApiContext, GridInitialState } from '@mui/x-data-grid-pro';
import { useUserSettings } from '../utils/use-user-settings';
import { Messages } from '../utils/message-constants';
import { availableHues } from './color-utils';

import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface GridStateCreateDialogProps {
  open: boolean;
  handleClose: () => void;
  module: string;
  section: string;
  gridStateKey: GridStateKey;
}

export default function GridStateCreateDialog({
  open,
  handleClose,
  module,
  section,
  gridStateKey,
}: GridStateCreateDialogProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  //   can only be used in a grid context like a toolbar
  const apiRef = useGridApiContext();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const { viewType } = useUserSettings();
  const [viewName, setViewName] = useState<string>('');

  const shade = theme.palette.mode === 'light' ? 300 : 500;

  const colors = availableHues.map(({ name, hue }) => {
    return {
      name,
      color: hue[shade],
    };
  });

  const [selectedColor, setSelectedColor] = useState<string>(colors[0].name);

  const viewNameExists = viewType[gridStateKey]?.savedGridStates.some(
    (state) => state.name === viewName
  );

  const viewNameIsDefault = viewName.toLowerCase().trim() === 'default';

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ViewType) => saveViewType(data),
    onSuccess: (_returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Saved('Grid view'),
      });

      queryClient.setQueryData<UserSettings>([UserKeys.UserSettings], (old) => {
        if (!old) return undefined;
        return {
          ...old,
          viewType: data,
        };
      });

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Saving('grid view'),
      });
    },
    onSettled: () => {
      createEvent(analytics.grid_state.create_grid_state, {
        module: module,
        section: section,
      });
    },
  });

  return (
    <GeneralDialog
      disableRestoreFocus={true}
      open={open}
      title={`Save New Grid View`}
      handleClose={handleClose}
      dialogContent={
        <Stack
          sx={{
            px: 2,
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
          gap={2}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Enter a name for your new grid view. This will save down grid
            sorting, filtering, column order & visibility, and page size. This
            is specific to you and not a domain wide view.
          </Typography>
          <TextField
            label="View Name"
            value={viewName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setViewName(e.target.value)
            }
            slotProps={{
              htmlInput: {
                maxLength: 25,
              },
            }}
            error={viewNameExists || viewNameIsDefault}
            helperText={
              viewNameExists
                ? 'View name already exists'
                : viewNameIsDefault
                ? 'Default view name is not allowed'
                : `${viewName.length}/25 characters`
            }
            autoFocus
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Select a color for your new grid view.
          </Typography>
          <Grid
            container
            columns={8}
            rowSpacing={0.5}
            sx={{
              width: '50%',
              mx: 'auto',
              p: 1,
            }}
          >
            {colors.map(({ name, color }) => (
              <Grid size={{ xs: 1, md: 1 }} key={name}>
                <ButtonBase
                  sx={{
                    width: 25,
                    height: 25,
                    backgroundColor: color,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border:
                      selectedColor === name
                        ? `2px solid ${theme.palette.text.primary}`
                        : 'none',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                  onClick={() => setSelectedColor(name)}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: () => {
          const currentState: GridInitialState = apiRef.current.exportState();

          const stateToSave: GridInitialState = { ...currentState };
          if (stateToSave.pagination?.paginationModel) {
            stateToSave.pagination = {
              ...stateToSave.pagination,
              paginationModel: {
                ...stateToSave.pagination.paginationModel,
                page: 0,
              },
            };
          }

          const savedGridStates = viewType[gridStateKey]?.savedGridStates || [];
          mutate({
            ...viewType,
            [gridStateKey]: {
              ...viewType[gridStateKey],
              activeGridState: viewName,
              savedGridStates: [
                ...savedGridStates,
                {
                  name: viewName,
                  createdDate: Date.now(),
                  color: selectedColor,
                  value: JSON.stringify(stateToSave),
                },
              ],
            },
          });
        },
        loading: isPending,
        disabled: viewName.length === 0 || viewNameExists || viewNameIsDefault,
      }}
    />
  );
}
